import api from "./client";

// ============= Types =============

export type StatusInventario =
	| "PLANEJADO"
	| "LIBERADO"
	| "EM_ANDAMENTO"
	| "ENCERRADO"
	| "CANCELADO";

export type TipoInventario =
	| "GERAL"
	| "CICLICO"
	| "POR_POSICAO"
	| "POR_SKU"
	| "AD_HOC";

/**
 * Onde a campanha é contada — decide as regras de sessão (#1596).
 * WEB: bipador + monitor, 2ª contagem pela mesma dupla.
 * MOBILE: só bipador, 2ª contagem pelo mesmo operador.
 */
export type OrigemInventario = "WEB" | "MOBILE";

export type StatusSessao = "ATIVA" | "ENCERRADA";

export type StatusContagemPosicao =
	| "EM_ANDAMENTO"
	| "CONCLUIDA"
	| "ABANDONADA";

export interface InventarioApi {
	id: number;
	nome: string;
	tipo: TipoInventario;
	status: StatusInventario;
	cega: boolean;
	toleranciaQuantidade: number;
	toleranciaPercentual: number;
	timeoutReservaMinutos: number;
	armazemId: number;
	usuarioCriadorId: number;
	dataCriacao: string;
	dataInicio: string | null;
	dataEncerramento: string | null;
	motivoCancelamento: string | null;
	/** 1 = validação simples (1ª contagem aplica direto). 2 = dual (padrão). */
	numeroMinimoContagens: 1 | 2;
	/** Onde a campanha é contada. Campanhas anteriores à feature vêm WEB. */
	origem: OrigemInventario;
}

export interface InventarioEscopoApi {
	id: number;
	inventarioId: number;
	localizacaoId: number;
	motivo: string | null;
	dataInclusao: string;
}

export interface SessaoInventarioApi {
	id: number;
	inventarioId: number;
	usuarioBipadorId: number;
	usuarioMonitorId: number;
	status: StatusSessao;
	dataAbertura: string;
	dataEncerramento: string | null;
	ultimaAtividade: string;
}

export interface SnapshotSaldoItem {
	produtoId: number;
	quantidade: number;
}

export interface ContagemPosicaoApi {
	id: number;
	inventarioId: number;
	sessaoId: number;
	localizacaoId: number;
	numeroContagem: number;
	status: StatusContagemPosicao;
	snapshot: { itens: SnapshotSaldoItem[]; capturadoEm: string };
	dataInicio: string;
	dataFinalizacao: string | null;
	ultimaAtividade: string;
}

export interface ContagemItemApi {
	id: number;
	contagemPosicaoId: number;
	produtoId: number;
	quantidade: number;
	clientRequestId: string;
	criadoEm: string;
	/**
	 * Lookup do produto (SKU, EAN, descrição, foto) enriquecido pelo
	 * backend. Vem null quando o produto não pôde ser resolvido (deletado).
	 * Endpoint listarItensContagem retorna esse campo desde o fix do
	 * "Continuar contagem" que mostrava só o id na lista.
	 */
	produto?: ProdutoLookupApi | null;
}

export interface LocalizacaoLookupApi {
	id: number;
	ean: string;
	nome: string;
	tipoNome: string | null;
	armazemNome: string | null;
}

export interface IniciarContagemResultado {
	contagem: ContagemPosicaoApi;
	localizacao: LocalizacaoLookupApi;
}

// ============= Inventário (campanha) =============

export type ParametrosEscopoApi =
	| { tipo: "POR_SKU"; sku: string }
	| { tipo: "POR_POSICAO"; localizacoes: number[] }
	| { tipo: "AD_HOC"; localizacoes: number[] };

export async function criarInventario(dto: {
	nome: string;
	tipo: TipoInventario;
	armazemId: number;
	cega?: boolean;
	toleranciaQuantidade?: number;
	toleranciaPercentual?: number;
	timeoutReservaMinutos?: number;
	parametrosEscopo?: ParametrosEscopoApi | null;
	numeroMinimoContagens?: 1 | 2;
	origem?: OrigemInventario;
}): Promise<InventarioApi> {
	const res = await api.post("/inventario", dto);
	return res.data;
}

/**
 * Troca WEB/MOBILE. O backend recusa depois que a campanha teve a
 * primeira localização bipada (#1596).
 */
export async function alterarOrigemInventario(
	id: number,
	origem: OrigemInventario,
): Promise<InventarioApi> {
	const res = await api.patch(`/inventario/${id}/origem`, { origem });
	return res.data;
}

export async function listarInventarios(params?: {
	armazemId?: number;
	status?: StatusInventario;
}): Promise<InventarioApi[]> {
	const res = await api.get("/inventario", { params });
	return res.data;
}

export async function buscarInventario(id: number): Promise<InventarioApi> {
	const res = await api.get(`/inventario/${id}`);
	return res.data;
}

export async function liberarInventario(id: number): Promise<InventarioApi> {
	const res = await api.patch(`/inventario/${id}/liberar`);
	return res.data;
}

export async function encerrarInventario(
	id: number,
	opts?: { forcar?: boolean },
): Promise<InventarioApi> {
	const res = await api.patch(`/inventario/${id}/encerrar`, {
		forcar: opts?.forcar ?? false,
	});
	return res.data;
}

export async function cancelarInventario(
	id: number,
	motivo: string,
): Promise<InventarioApi> {
	const res = await api.patch(`/inventario/${id}/cancelar`, { motivo });
	return res.data;
}

// ============= Escopo =============

export async function definirEscopo(
	inventarioId: number,
	dto: { localizacaoIds: number[]; motivo?: string },
): Promise<InventarioEscopoApi[]> {
	const res = await api.post(`/inventario/${inventarioId}/escopo`, dto);
	return res.data;
}

export async function listarEscopo(
	inventarioId: number,
): Promise<InventarioEscopoApi[]> {
	const res = await api.get(`/inventario/${inventarioId}/escopo`);
	return res.data;
}

export async function removerPosicaoEscopo(
	inventarioId: number,
	localizacaoId: number,
): Promise<void> {
	await api.delete(`/inventario/${inventarioId}/escopo/${localizacaoId}`);
}

// ============= Sessão =============

export async function abrirSessao(
	inventarioId: number,
	dto: { usuarioBipadorId: number; usuarioMonitorId: number },
): Promise<SessaoInventarioApi> {
	const res = await api.post(`/inventario/${inventarioId}/sessoes`, dto);
	return res.data;
}

export async function encerrarSessao(
	sessaoId: number,
): Promise<SessaoInventarioApi> {
	const res = await api.patch(`/inventario/sessoes/${sessaoId}/encerrar`);
	return res.data;
}

// ============= Contagem =============

export async function iniciarContagem(
	inventarioId: number,
	dto: {
		sessaoId: number;
		/** EAN da etiqueta da posição (preferido — é o que o operador bipa). */
		localizacaoEan?: string;
		/** Fallback: id interno da localização (uso administrativo). */
		localizacaoId?: number;
	},
): Promise<IniciarContagemResultado> {
	const res = await api.post(`/inventario/${inventarioId}/contagens`, dto);
	return res.data;
}

export interface ProdutoLookupApi {
	id: number;
	ean: string | null;
	sku: string;
	descricao: string | null;
	urlFoto: string | null;
}

export interface ResultadoRegistrarBipe {
	item: ContagemItemApi;
	produto: ProdutoLookupApi;
}

export async function bipar(
	contagemId: number,
	dto: {
		sessaoId: number;
		/** EAN ou SKU bipado (preferido — operador bipa código de barras). */
		produtoCodigo?: string;
		/** Fallback: id interno do produto. */
		produtoId?: number;
		quantidade: number;
		clientRequestId: string;
	},
): Promise<ResultadoRegistrarBipe> {
	const res = await api.post(`/inventario/contagens/${contagemId}/bipar`, dto);
	return res.data;
}

export type ResultadoComparacao =
	| "CONVERGENTE_1"
	| "AGUARDANDO_2A"
	| "CONVERGENTE_2"
	| "AGUARDANDO_3A"
	| "CONVERGENTE_3"
	| "RESET";

export interface DivergenciaItemApi {
	produtoId: number;
	qtdC1: number;
	qtdC2: number;
	qtdC3: number | null;
	qtdDecidida: number | null;
	convergiu: boolean;
}

export interface ComparacaoContagemApi {
	id: number;
	inventarioId: number;
	localizacaoId: number;
	resultado: ResultadoComparacao;
	divergencias: DivergenciaItemApi[];
	contagemIds: number[];
	parConvergente: [number, number] | null;
	dataDecisao: string;
	/**
	 * As contagens bateram, mas o ajuste de saldo foi recusado (#2100). A tela
	 * não pode dizer "concluída": o saldo continua errado.
	 */
	ajustePendente?: boolean;
	ultimoErroAjuste?: string | null;
}

export interface ResultadoFinalizarContagem {
	contagem: ContagemPosicaoApi;
	comparacao: ComparacaoContagemApi;
}

/**
 * `posicaoVazia` só é necessário quando nenhum produto foi bipado: o
 * backend recusa com CONTAGEM_SEM_ITENS até o operador confirmar que a
 * posição está vazia de propósito.
 */
export async function finalizarContagem(
	contagemId: number,
	sessaoId: number,
	posicaoVazia = false,
): Promise<ResultadoFinalizarContagem> {
	const res = await api.post(
		`/inventario/contagens/${contagemId}/finalizar`,
		{ sessaoId, posicaoVazia },
	);
	return res.data;
}

export async function listarItensContagem(
	contagemId: number,
): Promise<ContagemItemApi[]> {
	const res = await api.get(`/inventario/contagens/${contagemId}/itens`);
	return res.data;
}

export interface RemoverItemResultadoApi {
	itemId: number;
	produtoId: number;
	quantidade: number;
}

export async function removerItemContagem(
	contagemId: number,
	itemId: number,
	sessaoId: number,
): Promise<RemoverItemResultadoApi> {
	const res = await api.delete(
		`/inventario/contagens/${contagemId}/itens/${itemId}`,
		{ data: { sessaoId } },
	);
	return res.data;
}

export interface ResultadoRetomarContagemApi {
	contagem: ContagemPosicaoApi;
	localizacao: LocalizacaoLookupApi;
	sessaoTransferida: boolean;
}

export async function retomarContagem(
	inventarioId: number,
	dto: { sessaoId: number; localizacaoId: number },
): Promise<ResultadoRetomarContagemApi> {
	const res = await api.post(
		`/inventario/${inventarioId}/contagens/retomar`,
		dto,
	);
	return res.data;
}

// ============= Dashboard / Supervisor =============

export type StatusPosicao =
	| "PENDENTE"
	| "EM_CONTAGEM"
	| "AGUARDANDO_2A"
	| "AGUARDANDO_3A"
	| "VALIDADA"
	| "AJUSTE_PENDENTE"
	| "RESET";

export interface DashboardInventario {
	inventarioId: number;
	totalEscopo: number;
	totalPosicoesContadas: number;
	totalPosicoesValidadas: number;
	totalAjustePendente: number;
	totalAguardando2: number;
	totalAguardando3: number;
	totalEmAndamento: number;
	totalReset: number;
	totalForaDoEscopo: number;
	percentualConcluido: number;
}

export interface PosicaoInventarioDto {
	localizacaoId: number;
	nome: string | null;
	tipoNome: string | null;
	armazemNome: string | null;
	status: StatusPosicao;
	noEscopo: boolean;
	totalContagens: number;
	ultimoNumeroContagem: number | null;
	ultimoResultado: ResultadoComparacao | null;
	/** Por que o ajuste não entrou — só com status AJUSTE_PENDENTE. */
	motivoAjustePendente: string | null;
	dataUltimaAtividade: string | null;
}

export async function obterDashboardInventario(
	inventarioId: number,
): Promise<DashboardInventario> {
	const res = await api.get(`/inventario/${inventarioId}/dashboard`);
	return res.data;
}

export interface PaginaPosicoesInventario {
	results: PosicaoInventarioDto[];
	/** Total que casa com o filtro, ignorando limit/offset. */
	total: number;
}

export interface FiltroPosicoesDashboard {
	status?: StatusPosicao;
	/** Busca parcial por nome da posição. */
	search?: string;
	limit?: number;
	offset?: number;
}

/**
 * Listagem paginada de posições, para o painel do supervisor.
 *
 * Endpoint separado do `/posicoes` de propósito: aquele é consumido pelo
 * app e devolve array cru. Este pagina no banco, porque campanha GERAL
 * tem o armazém inteiro no escopo — milhares de posições.
 */
export async function listarPosicoesDashboard(
	inventarioId: number,
	filtro?: FiltroPosicoesDashboard,
): Promise<PaginaPosicoesInventario> {
	const res = await api.get(`/inventario/${inventarioId}/dashboard/posicoes`, {
		params: filtro,
	});
	return res.data;
}

export async function liberarPosicaoSupervisor(
	inventarioId: number,
	localizacaoId: number,
	motivo: string,
): Promise<{ contagensArquivadas: number; motivo: string }> {
	const res = await api.post(
		`/inventario/${inventarioId}/posicoes/${localizacaoId}/liberar`,
		{ motivo },
	);
	return res.data;
}

// ============= Relatório final =============

export interface AjusteResumoApi {
	ajusteId: number;
	localizacaoId: number;
	posicaoNome: string | null;
	comparacaoId: number;
	resultado: ResultadoComparacao;
	qtdItensAjustados: number;
	somaEntradas: number;
	somaSaidas: number;
	movimentacaoIds: number[];
	dataAplicacao: string;
}

export interface PosicaoNaoInventariadaApi {
	localizacaoId: number;
	nome: string | null;
	tipoNome: string | null;
	armazemNome: string | null;
}

export interface RelatorioFinalInventarioApi {
	inventario: InventarioApi;
	dashboard: DashboardInventario;
	ajustes: AjusteResumoApi[];
	pendencias: PosicaoInventarioDto[];
	posicoesNaoInventariadas: PosicaoNaoInventariadaApi[];
	totalMovimentacoes: number;
	totalUnidadesEntrada: number;
	totalUnidadesSaida: number;
}

export async function obterRelatorioFinal(
	inventarioId: number,
): Promise<RelatorioFinalInventarioApi> {
	const res = await api.get(`/inventario/${inventarioId}/relatorio-final`);
	return res.data;
}

// ============= Detalhe por posição =============

export interface ItemContagemDetalheApi {
	produtoId: number;
	produtoSku: string;
	produtoEan: string | null;
	produtoDescricao: string | null;
	quantidade: number;
	criadoEm: string;
}

export interface ContagemDetalheApi {
	contagemId: number;
	numeroContagem: number;
	status: StatusContagemPosicao;
	bipador: { id: number; nome: string } | null;
	monitor: { id: number; nome: string } | null;
	dataInicio: string;
	dataFinalizacao: string | null;
	itens: ItemContagemDetalheApi[];
}

export interface DetalhePosicaoApi {
	inventarioId: number;
	localizacaoId: number;
	localizacaoNome: string | null;
	tipoNome: string | null;
	armazemNome: string | null;
	contagens: ContagemDetalheApi[];
}

export async function obterDetalhePosicao(
	inventarioId: number,
	localizacaoId: number,
	signal?: AbortSignal,
): Promise<DetalhePosicaoApi> {
	const res = await api.get(
		`/inventario/${inventarioId}/posicoes/${localizacaoId}/detalhe`,
		{ signal },
	);
	return res.data;
}
